import { RedirectToSignIn, Show, SignIn } from '@clerk/react'
import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router'
import { DesignRoot, useDesign } from './designs/design-root'
import { ScreenNotInDesign } from './designs/not-in-design'
import type { DesignScreens } from './designs/types'
import { PreviewRoot } from './preview/preview-root'

const rootRoute = createRootRoute({ component: () => <Outlet /> })

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: () => (
    <div className="grid h-full place-items-center">
      <SignIn routing="hash" />
    </div>
  ),
})

// Public, no-auth: mock-data preview of all design languages (sibling of /sign-in, outside Clerk gating).
const previewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preview',
  component: PreviewRoot,
})

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: () => (
    <>
      <Show when="signed-in">
        <DesignRoot>
          <Outlet />
        </DesignRoot>
      </Show>
      <Show when="signed-out">
        <RedirectToSignIn />
      </Show>
    </>
  ),
})

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/locations' })
  },
})

const locationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/locations',
  component: function LocationsScreen() {
    const { screens } = useDesign()
    return <screens.locations />
  },
})

// Location-scoped screens: render the active design's screen, else the shared fallback.
// Screens read `:idOrCode` themselves via `useIdOrCode()` (app/designs/use-id-or-code.ts).
function designScreenRoute(path: string, key: keyof Omit<DesignScreens, 'locations'>) {
  return createRoute({
    getParentRoute: () => protectedRoute,
    path,
    component: function DesignScreen() {
      const { screens } = useDesign()
      const Screen = screens[key]
      return Screen ? <Screen /> : <ScreenNotInDesign name={key} />
    },
  })
}

const dashboardRoute = designScreenRoute('/dashboard', 'dashboard')
const locationDetailRoute = designScreenRoute('/locations/$idOrCode', 'locationDetail')
const networkRoute = designScreenRoute('/locations/$idOrCode/network', 'network')
const wanSettingsRoute = designScreenRoute('/locations/$idOrCode/network/wan-settings', 'wanSettings')
const managedDevicesRoute = designScreenRoute('/locations/$idOrCode/network/managed-devices', 'managedDevices')
const reservationsRoute = designScreenRoute('/locations/$idOrCode/network/reservations', 'reservations')
const switchPortsRoute = designScreenRoute('/locations/$idOrCode/network/switch-ports', 'switchPorts')
const clientsRoute = designScreenRoute('/locations/$idOrCode/network/clients', 'clients')
const posTerminalsRoute = designScreenRoute('/locations/$idOrCode/pos-systems/terminals', 'posTerminals')

const adminUsersRoute = designScreenRoute('/admin/users', 'adminUsers')
const adminRolesRoute = designScreenRoute('/admin/roles', 'adminRoles')
const adminPermissionsRoute = designScreenRoute('/admin/permissions', 'adminPermissions')
const adminTenantsRoute = designScreenRoute('/admin/tenants', 'adminTenants')
const adminTenantDetailRoute = designScreenRoute('/admin/tenants/$orgId', 'adminTenantDetail')
const adminApiTokensRoute = designScreenRoute('/admin/api-tokens', 'adminApiTokens')
const adminCredentialsRoute = designScreenRoute('/admin/credentials', 'adminCredentials')
const adminLocationsRoute = designScreenRoute('/admin/locations', 'adminLocations')
// `/admin` index -> credentials (v1 behavior for non-manager roles).
const adminIndexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin',
  beforeLoad: () => {
    // `href` (not `to`): designScreenRoute paths are typed `string`, so they are not in the typed route union.
    throw redirect({ href: '/admin/credentials' })
  },
})

const routeTree = rootRoute.addChildren([
  signInRoute,
  previewRoute,
  protectedRoute.addChildren([
    indexRoute,
    locationsRoute,
    dashboardRoute,
    locationDetailRoute,
    networkRoute,
    wanSettingsRoute,
    managedDevicesRoute,
    reservationsRoute,
    switchPortsRoute,
    clientsRoute,
    posTerminalsRoute,
    adminIndexRoute,
    adminUsersRoute,
    adminRolesRoute,
    adminPermissionsRoute,
    adminTenantsRoute,
    adminTenantDetailRoute,
    adminApiTokensRoute,
    adminCredentialsRoute,
    adminLocationsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
